import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckIcon } from "lucide-react";
import { useMutation, useQuery } from "@apollo/client";
import { cn } from "@/lib/utils";

import MountAndHide from "@/components/common/MountAndHide/MountAndHide";
import SelectServices from "@/pages/NewTaskPage/SelectServices/SelectServices";
import SelectSpecs from "@/pages/NewTaskPage/SelectSpecs/SelectSpecs";
import ListTasks, {
  TaskDetailsInputData,
} from "@/pages/NewTaskPage/ListTasks/ListTasks";
import CheckUploads from "@/pages/NewTaskPage/LinkUploadShots/LinkUploadShots";
import SelectVendorsInline from "./SelectVendorsInline";
import { Vendor, VENDORS_QUERY } from "@/components/common/SelectVendors";

import { TASK_SHOTS_QUERY, CREATE_SUB_TASK_MUTATION } from "./SubTask.query";
import { Service, FileAttachment } from "@/pages/NewTaskPage/types";
import { Shot } from "@/pages/NewTaskPage/SelectShots/SelectShots";
import { TaskShotPriority } from "@/__generated__/graphql";
import { MultipleServicesFormValues } from "@/pages/NewTaskPage/SelectSpecs/SelectSpecs";
import { TASK_SHOTS_QUERY_PARAM } from "@/helpers/constants";
import { buildUrl, PROJECT_PENDING_URL } from "@/helpers/url";

const SELECT_SERVICE_STEP = 1;
const SET_SPECS_STEP = 2;
const SELECT_VENDORS_STEP = 3;
const LIST_YOUR_TASKS = 4;
const REVIEW_SHOTS_STEP = 5;

const STEPS = [
  {
    step: SELECT_SERVICE_STEP,
    name: "Select Service",
    helpText: "Select the service you would like to subcontract",
  },
  {
    step: SET_SPECS_STEP,
    name: "Specifications",
    helpText: "Define the specifications for your sub task",
  },
  {
    step: SELECT_VENDORS_STEP,
    name: "Select Vendors",
    helpText: "Choose vendors for your subcontract",
  },
  {
    step: LIST_YOUR_TASKS,
    name: "List your tasks",
    helpText: "Input the tasks and upload reference files if needed",
  },
  {
    step: REVIEW_SHOTS_STEP,
    name: "Review Shots",
    helpText: "Review your shots and confirm everything is correct",
  },
];

interface SubTaskFlowProps {
  shots: Shot[];
  taskShotIds: string[];
  projectId: string;
}

const SubTaskFlow: React.FC<SubTaskFlowProps> = ({
  shots,
  taskShotIds,
  projectId,
}) => {
  const navigate = useNavigate();

  const [step, setStep] = useState(SELECT_SERVICE_STEP);
  const [service, setService] = useState<Service | null>(null);
  const [serviceSpecs, setServiceSpecs] = useState<MultipleServicesFormValues>(
    {},
  );
  const [taskDetails, setTaskDetails] = useState<TaskDetailsInputData[]>([]);
  const [fileAttachments, setFileAttachments] = useState<FileAttachment[]>([]);
  const [selectedVendors, setSelectedVendors] = useState<Vendor[]>([]);
  const [availableVendors, setAvailableVendors] = useState<Vendor[]>([]);
  const [formError, setFormError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  const linkedShots = useMemo(() => {
    if (!service) return {};
    return {
      [service.id]: shots,
    };
  }, [service, shots]);

  const shotDetailsState = useMemo(() => {
    if (!service) return {};
    return {
      [service.id]: shots.map((shot) => ({
        priority: TaskShotPriority.Medium,
        shotId: shot.id,
      })),
    };
  }, [service, shots]);

  const serviceReferenceFiles = useMemo(() => {
    return service
      ? {
          [service.id]: {
            additionalMediaKeys: [],
            fileAttachments,
            referenceShots: [],
          },
        }
      : {};
  }, [service, fileAttachments]);

  const [createSubTask] = useMutation(CREATE_SUB_TASK_MUTATION);

  const { data: vendorsData } = useQuery(VENDORS_QUERY, {
    variables: service ? { serviceId: service.id } : undefined,
    skip: !service,
  });

  React.useEffect(() => {
    if (vendorsData?.vendors?.edges) {
      const vendors = vendorsData.vendors.edges
        .map((edge: { node: Vendor }) => edge.node)
        .filter((v: Vendor) => v.id);
      setAvailableVendors(vendors);
    }
  }, [vendorsData]);

  useEffect(() => {
    if (submitting) {
      confirmTask();
    }
  }, [submitting, taskDetails, fileAttachments]);

  const onServiceSelect = (selectedService: Service) => {
    setService(selectedService);
    setStep(SET_SPECS_STEP);
  };

  const onSpecSelect = (
    values: MultipleServicesFormValues,
    context: { setSubmitting: (submitting: boolean) => void },
  ) => {
    const { setSubmitting } = context;
    setServiceSpecs(values);

    setStep(SELECT_VENDORS_STEP);

    setSubmitting(false);
  };

  const onVendorSelect = (vendor: Vendor) => {
    if (selectedVendors.find((v: Vendor) => v.id === vendor.id)) {
      setSelectedVendors((prev: Vendor[]) =>
        prev.filter((v: Vendor) => v.id !== vendor.id),
      );
    } else {
      setSelectedVendors((prev: Vendor[]) => [...prev, vendor]);
    }
  };

  const onVendorDeselect = (vendor: Vendor) => {
    setSelectedVendors((prev: Vendor[]) =>
      prev.filter((v: Vendor) => v.id !== vendor.id),
    );
  };

  const handleVendorSelectionComplete = () => {
    if (service?.shouldUseShotDetectionFlow) {
      setStep(REVIEW_SHOTS_STEP);
    } else {
      setStep(LIST_YOUR_TASKS);
    }
  };

  const handleVendorSelectionBack = () => {
    setStep(SET_SPECS_STEP);
  };

  const onListTasksComplete = (details: TaskDetailsInputData[]) => {
    setTaskDetails(details);
    setSubmitting(true);
  };

  const goToStep = (toStep: number) => {
    setStep(toStep);
  };

  const handleReset = () => {
    setFileAttachments((prev) => prev.map((f) => ({ ...f, selected: false })));
  };

  const confirmTask = async () => {
    if (!service || !projectId) {
      return;
    }

    setFormError(undefined);

    const serviceSpec = serviceSpecs[service.id] || {};

    try {
      const variables = {
        taskShotIds,
        projectId: projectId,
        vendorIds: selectedVendors.map((v) => v.id),
        serviceId: service.id,
        currency: serviceSpec.currency || "USD",
        deadline: serviceSpec.deadline || "",
        deliverables:
          serviceSpec.deliverables
            ?.filter((d) => d.formatId && d.specId)
            .map((d) => ({
              formatId: d.formatId!,
              specId: d.specId!,
            })) || [],
        taskDetails: service.shouldUseShotDetectionFlow
          ? undefined
          : taskDetails.map((taskDetail) => ({
              name: taskDetail.name,
              amount: taskDetail.amount,
              deliveryDate: taskDetail.deliveryDate,
              priority: taskDetail.priority,
              subServiceId: taskDetail.subServiceId,
            })),
        fileAttachmentIds: fileAttachments
          .filter((f) => f.selected)
          .map((f) => f.id),
      };

      const result = await createSubTask({ variables });

      if (result.data?.createSubTask?.success) {
        const url = buildUrl(PROJECT_PENDING_URL, {
          projectId: projectId,
          tab: "bidpackages",
        });
        navigate(url);
      } else {
        setFormError(
          result.data?.createSubTask?.message || "Failed to create sub task",
        );
      }
    } catch (error) {
      setFormError(
        error instanceof Error ? error.message : "An error occurred",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const hasShotDetection = service?.shouldUseShotDetectionFlow;
  const currentSteps = useMemo(() => {
    return STEPS.filter((s) => {
      if (hasShotDetection && s.step === LIST_YOUR_TASKS) {
        return false;
      }

      return true;
    });
  }, [hasShotDetection]);

  return (
    <div className="flex flex-col bg-white-main dark:bg-black-main overflow-hidden">
      <div className="bg-accordion mt-[20px] rounded-[24px] max-h-[calc(100%-98px)] overflow-hidden">
        <div className="mx-[24px] mt-[24px] flex justify-between items-center pb-6">
          <div className="flex flex-col gap-2">
            <h3 className="text-[20px] text-primary-foreground">
              Add a New Sub Task
            </h3>
            <p className="text-sm text-secondary-foreground">
              {STEPS[step - 1].helpText}
            </p>
          </div>
          <div className="flex gap-2">
            {currentSteps.map((s, index) => (
              <div
                key={s.step}
                className={cn(
                  "flex gap-[10px] items-center text-xs text-primary-foreground p-[8px] border border-accent-foreground rounded-[99px] cursor-pointer",
                  s.step === step && "border-paint text-service-paint",
                )}
                onClick={() => goToStep(s.step)}
              >
                <p
                  className={cn(
                    "w-6 h-6 p-[4px] bg-secondary rounded-full text-center flex items-center justify-center",
                    s.step === step && "!bg-service-paint text-white",
                    step > s.step && "!bg-service-paint text-white",
                  )}
                >
                  {step > s.step ? (
                    <CheckIcon className="w-4 h-4" />
                  ) : (
                    index + 1
                  )}
                </p>
                <p>{s.name}</p>
              </div>
            ))}
          </div>
        </div>

        {formError && (
          <div className="mx-[24px] mb-4 bg-destructive/10 border border-destructive/30 text-destructive px-6 py-4 rounded-lg">
            {formError}
          </div>
        )}

        <div className=" px-[24px] pb-[24px]">
          <MountAndHide show={step === SELECT_SERVICE_STEP}>
            <SelectServices
              selectedServices={service ? [service] : []}
              onServiceSelect={onServiceSelect}
              onNext={() => goToStep(SET_SPECS_STEP)}
            />
          </MountAndHide>

          <MountAndHide show={step === SET_SPECS_STEP}>
            {service && (
              <SelectSpecs
                projectId={projectId}
                serviceIds={[service.id]}
                onNext={onSpecSelect}
                showCurrencyField={true}
                timezone={
                  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
                }
              />
            )}
          </MountAndHide>

          <MountAndHide show={step === SELECT_VENDORS_STEP}>
            {service && (
              <SelectVendorsInline
                vendors={availableVendors}
                selectedVendors={selectedVendors}
                onVendorSelect={onVendorSelect}
                onVendorDeselect={onVendorDeselect}
                onNext={handleVendorSelectionComplete}
                onBack={handleVendorSelectionBack}
                limit={10}
              />
            )}
          </MountAndHide>

          <MountAndHide show={step === LIST_YOUR_TASKS}>
            {service && (
              <ListTasks
                service={service}
                subServices={service.subServices || []}
                projectId={projectId}
                fileAttachments={fileAttachments}
                updateFileAttachments={setFileAttachments}
                updateTaskDetails={onListTasksComplete}
                onNext={() => {}}
              />
            )}
          </MountAndHide>

          <MountAndHide show={step === REVIEW_SHOTS_STEP}>
            <CheckUploads
              projectId={projectId}
              shots={linkedShots}
              shotDetails={shotDetailsState}
              services={service ? [service] : []}
              updateShots={() => {}}
              setNewUploadData={() => {}}
              onNext={() => setSubmitting(true)}
              onReset={handleReset}
              serviceReferenceFiles={serviceReferenceFiles}
              updateServiceReferenceFiles={(_, updates) => {
                if (updates.fileAttachments) {
                  setFileAttachments(updates.fileAttachments);
                }
              }}
              forSubTask
              submitting={submitting}
            />
          </MountAndHide>
        </div>
      </div>
    </div>
  );
};

const SubTaskFlowContainer: React.FC = () => {
  const { project_id } = useParams<{
    project_id: string;
  }>();

  const urlParams = new URLSearchParams(window.location.search);
  const taskShotIdsParam = urlParams.get(TASK_SHOTS_QUERY_PARAM);
  const taskShotIds = taskShotIdsParam ? taskShotIdsParam.split(",") : [];

  const {
    data: shotsData,
    loading: shotsLoading,
    error,
  } = useQuery(TASK_SHOTS_QUERY, {
    variables: { taskShotIds },
    skip: taskShotIds.length === 0,
  });

  if (shotsLoading) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        Loading shots...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-destructive">
        Error loading shots: {error.message}
      </div>
    );
  }

  if (!shotsData || !shotsData.taskShots) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        No shots found
      </div>
    );
  }

  const shots = shotsData.taskShots.map((ts: { shot: Shot }) => ts.shot);

  return (
    <SubTaskFlow
      shots={shots}
      taskShotIds={taskShotIds}
      projectId={project_id!}
    />
  );
};

export default SubTaskFlowContainer;
